import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const SplashScreen = () => {
  const navigate = useNavigate();
  const [showContent, setShowContent] = useState(false);
  const [autoRedirect, setAutoRedirect] = useState(false);

  useEffect(() => {
    // Show content after initial load
    const contentTimer = setTimeout(() => {
      setShowContent(true);
    }, 500);

    // Auto-redirect after 4 seconds if user doesn't interact
    const redirectTimer = setTimeout(() => {
      setAutoRedirect(true);
      navigate('/login');
    }, 4000);

    return () => {
      clearTimeout(contentTimer);
      clearTimeout(redirectTimer);
    };
  }, [navigate]);

  const handleGetStarted = () => {
    setAutoRedirect(true);
    navigate('/login');
  };

  const handleLearnMore = () => {
    // For now, navigate to dinner planning to show features
    navigate('/dinner-planning');
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const floatingAnimation = {
    animate: {
      y: [0, -10, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted to-accent/10 flex flex-col items-center justify-center px-4 py-8 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-20 -left-20 w-40 h-40 bg-primary/5 rounded-full blur-xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute -bottom-20 -right-20 w-60 h-60 bg-accent/10 rounded-full blur-xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>
      {/* Main content */}
      <motion.div
        className="text-center z-10 max-w-md mx-auto"
        variants={staggerChildren}
        initial="initial"
        animate={showContent ? "animate" : "initial"}
      >
        {/* Logo and Brand */}
        <motion.div variants={fadeInUp} className="mb-8">
          <motion.div
            className="inline-flex items-center justify-center w-20 h-20 bg-primary rounded-2xl shadow-warm-lg mb-6"
            variants={floatingAnimation}
            animate="animate"
          >
            <Icon
              name="Utensils"
              size={32}
              className="text-primary-foreground"
            />
          </motion.div>
          
          <motion.h1
            className="text-4xl font-bold text-foreground mb-2 tracking-tight"
            variants={fadeInUp}
          >
            DinUP
          </motion.h1>
          
          <motion.p
            className="text-lg text-muted-foreground font-medium"
            variants={fadeInUp}
          >
            Where Memories Meet Meals
          </motion.p>
        </motion.div>

        {/* Tagline */}
        <motion.div variants={fadeInUp} className="mb-8">
          <p className="text-base text-muted-foreground leading-relaxed">
            Plan unforgettable dinner parties, manage guests, and create culinary experiences that bring people together.
          </p>
        </motion.div>

        {/* Animated Feature Icons */}
        <motion.div
          variants={fadeInUp}
          className="flex justify-center gap-6 mb-10"
        >
          <motion.div
            className="flex flex-col items-center gap-2"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <div className="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center">
              <Icon name="Calendar" size={20} className="text-primary" />
            </div>
            <span className="text-xs text-muted-foreground font-medium">Plan</span>
          </motion.div>
          
          <motion.div
            className="flex flex-col items-center gap-2"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <div className="w-12 h-12 bg-success/20 rounded-xl flex items-center justify-center">
              <Icon name="Users" size={20} className="text-success" />
            </div>
            <span className="text-xs text-muted-foreground font-medium">Connect</span>
          </motion.div>
          
          <motion.div
            className="flex flex-col items-center gap-2"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <div className="w-12 h-12 bg-warning/20 rounded-xl flex items-center justify-center">
              <Icon name="Heart" size={20} className="text-warning" />
            </div>
            <span className="text-xs text-muted-foreground font-medium">Enjoy</span>
          </motion.div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          variants={fadeInUp}
          className="flex flex-col gap-3 w-full"
        >
          <Button
            size="lg"
            className="w-full text-base font-medium"
            onClick={handleGetStarted}
            iconName="ArrowRight"
            iconPosition="right"
          >
            Get Started
          </Button>
          
          <Button
            variant="ghost"
            size="lg"
            className="w-full text-base font-medium"
            onClick={handleLearnMore}
          >
            Learn More
          </Button>
        </motion.div>

        {/* Loading indicator during auto-redirect */}
        {autoRedirect && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground"
          >
            <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            <span>Taking you to login...</span>
          </motion.div>
        )}
      </motion.div>
      {/* Bottom brand mark */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: showContent ? 0.5 : 0 }}
        transition={{ delay: 1.5, duration: 0.5 }}
      >
        <p className="text-xs text-muted-foreground">
          © 2024 DinUP. Making dinners memorable.
        </p>
      </motion.div>
    </div>
  );
};

export default SplashScreen;